import { faInfoCircle, faPlus, faPenToSquare, faSpinner, faUserShield, faCircleXmark, faFileInvoice, faDownload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getSaiTerminology } from '../../../config/terminology'
import { useEffect, useState } from 'react'

import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI'
import useAxiosPrivate from '../../../hooks/useAxiosPrivate'
import useAuth from '../../../hooks/useAuth'

import moment from 'moment'

import SideBar from '../../SideBar'

const Accounts = () => {
  const { auth, createInfo } = useAuth()

  const axiosSai = useAxiosPrivateSAI()
  const axios = useAxiosPrivate()
  const today = moment()

  const [policyData, setPolicyData] = useState(null)
  const [claims, setClaims] = useState([])
  const [outstandingTotal, setOutstandingTotal] = useState(null)

  const [showEdit, setShowEdit] = useState(false)
  const [showCode, setShowCode] = useState(false)
  const [responseCode, setResponseCode] = useState('')
  const [accountName, setAccountName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [emailRemittance, setEmailRemittance] = useState('')
  const [paymentReference, setPaymentReference] = useState('')
  const [inputCode, setInputCode] = useState('')
  const [sortCode, setSortCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState([])

  const expiryDate = moment(policyData?.policy_expire)
  const daysRemaining = expiryDate.diff(today, 'days')
  const daysElapsed = 364 - daysRemaining

  const progressPercent = Math.min((daysElapsed / 364) * 100, 100)
  const terms = getSaiTerminology(policyData)
  const totalClaims = claims.length

  const handleSortCodeChange = (event) => {
    const inputValue = event.target.value
    const cursorPosition = event.target.selectionStart
    const cleanedValue = inputValue.replace(/\D/g, '')
    const formattedValue =
      cleanedValue
        .match(/.{1,2}/g)
        ?.join('-')
        .slice(0, 8) || ''

    let newCursorPosition = cursorPosition
    const dashesBeforeCursor = Math.floor((cursorPosition - 1) / 3)
    const dashesInFormattedValue = (
      formattedValue.slice(0, cursorPosition).match(/-/g) || []
    ).length
    newCursorPosition += dashesInFormattedValue - dashesBeforeCursor
    setSortCode(formattedValue)
    requestAnimationFrame(() => {
      event.target.setSelectionRange(newCursorPosition, newCursorPosition)
    })
  }

  const toggleEdit = () => {
    setShowEdit(!showEdit)
  }

  useEffect(() => {
    const localPolicy = JSON.parse(localStorage.getItem('policyData'))
    if (!localPolicy?.policy_id) return

    const applyPolicy = (policy) => {
      setPolicyData(policy)
      setAccountName(policy.payment_name || '')
      setAccountNumber(policy.payment_account_number || '')
      setEmailRemittance(policy.email_remittance_address || '')
      setPaymentReference(policy.payment_reference || '')
      setSortCode(formatSortCode(policy.payment_sort_code || ''))
    }

    applyPolicy(localPolicy)

    fetchPolicy(localPolicy.policy_id)
      .then(freshPolicyData => {
        console.log(freshPolicyData)
        const updatedPolicy = { ...localPolicy, ...freshPolicyData.data }
        localStorage.setItem('policyData', JSON.stringify(updatedPolicy))
        applyPolicy(updatedPolicy)
      })
      .catch(err => {
        console.error('Policy fetch error:', err)
      })

    axios.get(`v1/staff-hub/policy/invoices/${localPolicy.policy_id}`)
      .then(res => {
        const sentInvoices = res.data.invoices.filter(invoice => {
          return invoice.invoice_status === 'sent'
        })
        const totalCost = sentInvoices
          .reduce((total, invoice) => {
            return total + parseFloat(invoice.invoice_cost || 0)
          }, 0)
          .toFixed(2)
        setInvoices(sentInvoices)
        setOutstandingTotal(totalCost || 0)
      })
      .catch(err => {
        console.error('Invoices fetch error:', err)
      })
  }, [])

  const formatSortCode = (value) => {
    const cleanedValue = value.replace(/\D/g, '')

    let formattedValue = ''
    if (cleanedValue.length <= 2) {
      formattedValue = cleanedValue
    } else if (cleanedValue.length <= 4) {
      formattedValue = `${cleanedValue.slice(0, 2)}-${cleanedValue.slice(2)}`
    } else {
      formattedValue = `${cleanedValue.slice(0, 2)}-${cleanedValue.slice(
        2,
        4
      )}-${cleanedValue.slice(4, 6)}`
    }
    return formattedValue
  }

  const updateBacs = async () => {
    const payload = {
      bacs: {
        payment_name: accountName,
        payment_account_number: accountNumber,
        payment_reference: paymentReference,
        payment_sort_code: sortCode.replace(/\D/g, ''),
        email_remittance_address: emailRemittance,
        policy_payment_type: 'BACS',
      },
      email: auth?.user?.contact_email,
    }

    try {
      setLoading(true)
      const res = await axiosSai.post(
        `/api/v1/staff-hub/accounts/auth/${policyData.policy_id}?api_token=${process.env.REACT_APP_KIS_TOKEN}`,
        { data: payload }
      )
      if (res.status === 200) {
        setShowEdit(false)
        setShowCode(true)
        setResponseCode(res.data.code)
      }
    } catch (err) {
      createInfo('error', 'Error updating account details')
    }
    setLoading(false)
  }

  const fetchPolicy = async (policyId) => {
    const res = await axiosSai.get(
      `/api/v1/staff-hub/accounts/policy/${policyId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`
    )
    console.log(res.data)
    return res.data;
  }

  const validateCode = async (inputCode) => {
    if (inputCode === responseCode) {
      try {
        const res = await axiosSai.post(
          `/api/v1/staff-hub/accounts/auth/${policyData.policy_id}/validate?api_token=${process.env.REACT_APP_KIS_TOKEN}`,
          {
            policy_id: policyData.policy_id,
            auth_code: inputCode
          }
        )
        if (res.status === 200) {
          setShowEdit(false)
          setShowCode(false)
          submitDetails()
        }
      } catch (err) {
        console.error('Error submitting unique code', err)
      }
    } else {
      createInfo('error', 'Incorrect code, please try again.')
      console.error('Input code does not match response code')
    }
  }

  const cancelCode = async () => {
    try {
      const res = await axiosSai.post(
        `/api/v1/staff-hub/accounts/auth/${policyData.policy_id}/cancel?api_token=${process.env.REACT_APP_KIS_TOKEN}`
      )
      if (res.status === 200) {
        setShowEdit(false)
        setShowCode(false)
      }
      createInfo('success', 'Code request has been cancelled!')
    } catch (err) {
      createInfo('error', 'Error submitting unique code')
    }
  }

  const submitDetails = async () => {
    const payload = {
      bacs: {
        payment_name: accountName,
        payment_account_number: accountNumber,
        payment_reference: paymentReference,
        payment_sort_code: sortCode.replace(/\D/g, ''),
        email_remittance_address: emailRemittance,
        policy_payment_type: 'BACS',
      },
      email: auth?.user?.contact_email,
    }

    try {
      setLoading(true)
      const res = await axiosSai.post(
        `/api/v1/staff-hub/accounts/auth/${policyData.policy_id}/accept?api_token=${process.env.REACT_APP_KIS_TOKEN}`,
        { data: payload }
      )
      if (res.status === 200) {
        setShowEdit(false)
        setShowCode(false)
      }
      createInfo('success', 'Account details updated successfully!')
    } catch (err) {
      createInfo('error', 'Error submitting details')
    }
    setLoading(false)
  }

  return (
    <div className="w-full">
      {/* Main Content Layout */}
      <div>
        {/* Main Card Section */}
        <main className="w-full space-y-4">
          <div className="p-6 max-w-5xl mx-auto space-y-6">
            <h2 className="text-xl font-semibold text-sas-dark">
              Account Information
            </h2>
            {/* Invoices */}
            <div>
              <h3 className="text-lg font-semibold text-sas-dark">
                Outstanding Invoices
              </h3>
              <div className=" mt-4">
                <table className="w-full text-sm text-center text-gray-800 border border-gray-300 shadow rounded-lg overflow-hidden">
                  <thead className="bg-sas text-white">
                    <tr>
                      <th className="p-2">Invoice Number</th>
                      <th className="p-2">Invoice Amount</th>
                      <th className="p-2">Invoice Date</th>
                      <th className="p-2">Due Date</th>
                      <th className="p-2">Invoice Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices?.length === 0 && (
                      <tr>
                        <td colSpan="5" className="p-2">
                          No outstanding invoices
                        </td>
                      </tr>
                    )}
                    {invoices?.map((invoice, index) => (
                      <tr
                        key={index}
                        className="border-t odd:bg-blue-50 even:bg-white"
                      >
                        <td className="p-2">{invoice?.invoice_reference}</td>
                        <td>£{invoice?.invoice_cost}</td>
                        <td>
                          {moment(invoice?.invoice_date).format('DD/MM/YYYY')}
                        </td>
                        <td>
                          <span
                            className={
                              moment(invoice?.due_at).isBefore(moment())
                                ? 'bg-red-100 text-red-800 rounded-full py-1 px-2'
                                : ''
                            }
                          >
                            {' '}
                            {moment(invoice?.due_at).format('DD/MM/YYYY')}
                          </span>
                        </td>
                        <td>
                          <a
                            href={`https://test.kisdigital.co.uk/invoice/${invoice.invoice_id}/pdf?token=BL3CLsO0FaBNVWuHPKDjGqqj`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <FontAwesomeIcon
                              icon={faDownload}
                              className="text-sas"
                            />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Card */}
            {!showCode && (
              <div className="bg-white shadow-md rounded-lg flex items-start border">
                <div className="grid grid-cols-3 gap-4 p-6 w-full">
                  <div>
                    <label className="block text-sm text-sas mb-1">
                      Account Name
                    </label>
                    {showEdit ? (
                      <div>
                        <input
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          type="text"
                          className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    ) : (
                      <div>{policyData?.payment_name}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-sas mb-1">
                      Account Number
                    </label>
                    {showEdit ? (
                      <div>
                        <input
                          maxLength="8"
                          value={accountNumber}
                          onChange={(e) => setAccountNumber(e.target.value)}
                          type="text"
                          className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    ) : (
                      <div>{`\u2022\u2022\u2022\u2022 ${policyData?.payment_account_number.slice(
                        -4
                      )}`}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-sas mb-1">
                      Sort Code
                    </label>
                    {showEdit ? (
                      <div>
                        <input
                          type="text"
                          value={sortCode}
                          onChange={handleSortCodeChange}
                          maxLength="8"
                          className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    ) : (
                      <div>
                        {policyData?.payment_sort_code.replace(
                          /\w/g,
                          ' \u2022 '
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-sas mb-1">
                      Email Remittance Address
                    </label>
                    {showEdit ? (
                      <div>
                        <input
                          value={emailRemittance}
                          onChange={(e) => setEmailRemittance(e.target.value)}
                          type="email"
                          className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    ) : (
                      <div>{policyData?.email_remittance_address}</div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm text-sas mb-1">
                      Payment Reference
                    </label>
                    {showEdit ? (
                      <div>
                        <input
                          value={paymentReference}
                          onChange={(e) => setPaymentReference(e.target.value)}
                          type="text"
                          className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    ) : (
                      <div>{policyData?.payment_reference}</div>
                    )}
                  </div>
                  {showEdit && (
                    <div className="col-span-full flex justify-end mt-2">
                      {loading ? (
                        <button
                          disabled
                          className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                        >
                          <FontAwesomeIcon
                            icon={faSpinner}
                            spin
                            className="mr-2"
                          />
                          Loading...
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={updateBacs}
                          className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium"
                        >
                          <spa className="px-4 py-2">Update Details</spa>
                          <span className="bg-sas text-white px-3 py-2">
                            <FontAwesomeIcon icon={faPlus} />
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div
                  onClick={() => {
                    toggleEdit()
                  }}
                  className="flex justify-end pt-3 pr-3"
                >
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    className="text-sas h-5 w-5"
                  />
                </div>
              </div>
            )}
            <div>
              {showCode && (
                <div className=" bg-white shadow-md rounded-lg p-6 mt-4">
                  <div className="flex justify-center mb-4 ">
                    <FontAwesomeIcon
                      icon={faUserShield}
                      className="h-10 w-10 rounded-full bg-sas p-6 text-white"
                    />
                  </div>
                  <p className="text-center py-2">
                    We have sent a one time security code to
                    ginnie.richichi@uk-sas.co.uk.
                  </p>
                  <p className="text-center pb-2">
                    Please enter the code below before we update your details.
                  </p>
                  <div className="my-4 w-3/4 mx-auto">
                    <input
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value)}
                      placeholder="xxxx"
                      className="w-full border border-sas rounded px-3 py-2 text-sm outline-none"
                    />
                  </div>
                  <div className="col-span-full flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={cancelCode}
                      className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium mr-4"
                    >
                      <span className="px-4 py-2">Cancel</span>
                      <span className="bg-sas text-white px-3 py-2">
                        <FontAwesomeIcon icon={faCircleXmark} />
                      </span>
                    </button>
                    {loading ? (
                      <button
                        disabled
                        className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                      >
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Loading...
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => validateCode(inputCode)}
                        className="inline-flex items-center border border-sas text-sas rounded-md overflow-hidden text-sm font-medium"
                      >
                        <span className="px-4 py-2">Validate</span>
                        <span className="bg-sas text-white px-3 py-2">
                          <FontAwesomeIcon icon={faPlus} />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Info Banner */}
            <div className="mt-8 flex items-start bg-red-100 border border-red-300 text-red-800 px-4 py-3 rounded shadow text-sm">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="mr-2 mt-1 text-red-800"
              />
              <p>
                If you would like to receive cheque payments, please contact our
                accounts team on 01773 814400.
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default Accounts
